import React, { useEffect, useMemo } from "react";
import {
  BlueBirdTheme, Button, Loading, Radio, Typography,
} from "@kitbird/core";
import { ViewIcon } from "../../../../../assets/icons";
import { ColumnI } from "../../../../../components/Table/TableProps";
import Table from "../../../../../components/Table";
import Status, { StatusEnums } from "../../../../../components/Status";
import useCloseReceipt from "../../useCloseReceipt";
import { formatUTCDate, toRupiah, toStringDate } from "../../../../../utils/common";
import { StickyTable } from "../../../../../components/Table/Table.style";
import { GetTrxRedReceiptParamsI } from "../../../../../services/trxRedReceiptService";
import { ReactComponent as PaperListIcon } from "../../../../../assets/icons/paper_list.svg";
import { ReactComponent as CheckIcon } from "../../../../../assets/icons/check_mark.svg";

interface RedReceiptTableProps {
  useCustomHook: ReturnType<typeof useCloseReceipt>;
}

const {
  blueGray, gray, primary, success,
} = BlueBirdTheme.colors;

const RedReceiptTable = ({ useCustomHook }: RedReceiptTableProps) => {
  const {
    pageName,
    meta,
    obsRef,
    tableRef,
    selectedRows,
    setSelectedRows,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    redReceiptList,
    setDetailRedReceipt,
    setCloseConfirm,
  } = useCustomHook;

  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  const renderRadioButton = (item: any, row: Record<string, any>) => (
    <Radio
      id={item}
      value={item}
      checked={!!selectedRows.find((data) => data.trx_id_bm === item)}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedRows([row as GetTrxRedReceiptParamsI]);
      }}
    />
  );

  const renderExpiry = (item: any, row: Record<string, any>) => {
    const isOnVerificationProcess = row.status_trx_info === "VERIFIKASI";
    const formattedDate = formatUTCDate(item);
    let statusContent = null;
    let statusEnum = null;
    let textColorAsBgColor = false;

    if (isOnVerificationProcess) {
      statusContent = "Menunggu verifikasi";
      statusEnum = StatusEnums.PRIMARY;
      textColorAsBgColor = true;
    } else if (row.warning === "Y") {
      statusContent = "Kadaluarsa";
      statusEnum = StatusEnums.DANGER;
    } else if (row.warning === "N") {
      statusContent = "Aktif";
      statusEnum = StatusEnums.SUCCESS;
    }

    const shouldRenderStatus = !!statusContent;

    return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", gap: 4 }}>
        {shouldRenderStatus && (
          <Status
            content={statusContent}
            status={statusEnum ?? undefined}
            textColorAsBgColor={textColorAsBgColor}
          />
        )}
        {!!row.jumlah_ijir && row.jumlah_ijir !== "0" && (
          <Status
            content={`Ijir ke-${row.jumlah_ijir}`}
            status={StatusEnums.PRIMARY}
            textColorAsBgColor
          />
        )}
      </div>
      <Typography level={7} color={gray.subtitle}>
        {formattedDate && `Kadaluarsa : ${formattedDate}`}
      </Typography>
    </div>;
  };

  const renderCloseBon = (item: any) => (
    <Status
      content={String(item).toLowerCase()}
      status={StatusEnums.INACTIVE}
      className="capitalize"
      style={{
        color: gray.subtitle,
      }}
    />
  );

  const renderExtendStatus = (item: any) => (
    <div style={{ textAlign: "center" }}>
      {(item && item !== "0") ? item : "-"}
    </div>
  );

  const renderComparation = (item: any, row: Record<string, any>) => {
    let differenceStatus: "EQUAL" | "MORE" | "LESS" = "EQUAL";
    const difference = Number(row.total_bon_putih_ijiran) - Number(row.total_bon_merah);
    if (difference > 0) differenceStatus = "MORE";
    else if (difference < 0) differenceStatus = "LESS";
    const val = {
      EQUAL: { label: "Pas", status: StatusEnums.INACTIVE },
      MORE: { label: "Lebih", status: StatusEnums.WARNING },
      LESS: { label: "Kurang", status: StatusEnums.RATING },
    }[differenceStatus];
    return <Status content={val.label} status={val.status} />;
  };

  const renderCreatorName = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.employee_id}</Typography>
    </div>
  );

  const renderCreatorArea = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.pool_id}</Typography>
    </div>
  );

  const renderCreatorDepartment = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.job_title}</Typography>
    </div>
  );

  const renderCreatorCompany = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.company_relation}</Typography>
    </div>
  );

  const columns: ColumnI[] = useMemo(() => [
    {
      title: "",
      dataIndex: "trx_id_bm",
      key: "radioButton",
      width: "55px",
      render: renderRadioButton,
      sticky: "left",
    },
    {
      title: "Masa bon merah",
      dataIndex: "aging_date",
      key: "aging_date",
      width: "260px",
      render: renderExpiry,
    },
    {
      title: "Status close bon",
      dataIndex: "status_trx_info",
      key: "status_trx_info",
      width: "150px",
      render: renderCloseBon,
    },
    {
      title: "Perpanjangan bon ke",
      dataIndex: "extended_bm",
      key: "extended_bm",
      width: "150px",
      render: renderExtendStatus,
    },
    {
      title: "No. bon merah",
      dataIndex: "trx_id_bm",
      key: "trx_id_bm",
      width: "200px",
    },
    {
      title: "Tanggal pengajuan",
      dataIndex: "create_at",
      key: "create_at",
      width: "160px",
      render: (item) => toStringDate(item),
    },
    {
      title: "Tanggal transfer",
      dataIndex: "transfer_date",
      key: "transfer_date",
      width: "160px",
      render: (item) => toStringDate(item),
    },
    {
      title: "Komparasi",
      dataIndex: "",
      key: "comparation",
      width: "200px",
      render: renderComparation,
    },
    {
      title: "Total bon merah",
      dataIndex: "total_bon_merah",
      key: "total_bon_merah",
      width: "200px",
      render: (item: any) => toRupiah(item),
    },
    {
      title: "Total bon putih ijiran",
      dataIndex: "total_bon_putih_ijiran",
      key: "total_bon_putih_ijiran",
      width: "200px",
      render: (item: any) => toRupiah(item),
    },
    {
      title: "Pemohon & NIP",
      dataIndex: "employee_name",
      key: "employee_name",
      width: "200px",
      render: renderCreatorName,
    },
    {
      title: "Wilayah & pool",
      dataIndex: "area_id",
      key: "area_id",
      width: "200px",
      render: renderCreatorArea,

    },
    {
      title: "Departemen & jabatan",
      dataIndex: "department",
      key: "department",
      width: "200px",
      render: renderCreatorDepartment,

    },
    {
      title: "Segmen",
      dataIndex: "segment_desc",
      key: "segment_desc",
      width: "200px",
    },
    {
      title: "Perusahaan & relasi",
      dataIndex: "company_code",
      key: "company_code",
      width: "200px",
      render: renderCreatorCompany,
    },
    {
      title: "Action",
      dataIndex: "trx_id",
      key: "actionCloseReceipt",
      width: "100px",
      sticky: "right",
      render: (item, row) => (
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={primary[300]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRedReceipt(row as GetTrxRedReceiptParamsI);
          }}
        >
          <ViewIcon width={16} height={16} />
        </Button>
      ),
    },
    {
      title: "Action",
      dataIndex: "trx_id",
      key: "actionVerificationReceipt",
      width: "150px",
      sticky: "right",
      render: (item, row) => (<div style={{ display: "flex", gap: 8 }}>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={blueGray.light}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRows([row as GetTrxRedReceiptParamsI]);
          }}
        >
          <PaperListIcon width={16} height={16} />
        </Button>
        <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={primary[400]}
          onClick={(e) => {
            e.stopPropagation();
            setDetailRedReceipt(row as GetTrxRedReceiptParamsI);
          }}
        >
          <ViewIcon width={16} height={16} />
        </Button>
        {row.status_trx === "VERIFIKASI" && <Button
          style={{ padding: "8px 10px" }}
          backgroundColor={success[300]}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRows([row as GetTrxRedReceiptParamsI]);
            setCloseConfirm(true);
          }}
        >
          <CheckIcon width={16} height={16} />
        </Button>}
      </div>),
    },
  ], [selectedRows]);

  const excludedKeysCloseReceipt = [
    "transfer_date",
    "actionVerificationReceipt",
  ];

  const excludedKeysVerificationReceipt = [
    "radioButton",
    "extended_bm",
    "create_at",
    "employee_name",
    "department",
    "company_code",
    "actionCloseReceipt",
  ];

  return (
    (isFetching && !isFetchingNextPage)
      ? <Loading variant="line" />
      : <StickyTable ref={tableRef}>
        <Table
          columns={pageName === "Verifikasi-bon"
            ? columns.filter((item) => !excludedKeysVerificationReceipt.includes(item.key))
            : columns.filter((item) => !excludedKeysCloseReceipt.includes(item.key))}
          dataSource={redReceiptList}
          keyId="trx_id_bm"
          selectedRowKeys={selectedRows.map((item) => item.trx_id_bm)}
          onClickRow={(row) => setSelectedRows([row as GetTrxRedReceiptParamsI])}
          footer={
            <>
              {(!isFetching && meta.page < meta.total_pages) && <tfoot ref={obsRef} style={{ height: 5, width: "100%" }} />}
              {isFetchingNextPage && <Loading variant="circular" />}
            </>
          }
        />
      </StickyTable>
  );
};

export default RedReceiptTable;
